import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Trash2, PlusCircle } from 'lucide-react';

import { servicesSchema, type ServicesValues, type ServiceRow } from '@/lib/validators/onboarding';
import { useOnboardingStore } from '@/lib/stores/onboarding';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Card, CardContent } from '@/components/ui/card';

export const Route = createFileRoute('/onboarding/services')({
  component: ServicesStep,
});

const TRADE_TYPE_OPTIONS = [
  { value: 'hisnik',       label: 'Hišnik / vzdrževalec' },
  { value: 'vodovod',      label: 'Vodovodar' },
  { value: 'elektrika',    label: 'Elektrikar' },
  { value: 'kljucavnicar', label: 'Ključavničar' },
  { value: 'mizar',        label: 'Mizar / pohištveni' },
  { value: 'avtomehanik',  label: 'Avtomehanik' },
  { value: 'cistilec',     label: 'Čistilni servis' },
  { value: 'frizer',       label: 'Frizer / kozmetik' },
  { value: 'drugo',        label: 'Drugo' },
];

const UNIT_OPTIONS = [
  { value: 'kos', label: 'kos' },
  { value: 'ura', label: 'ura' },
  { value: 'm',   label: 'm' },
  { value: 'm2',  label: 'm²' },
  { value: 'paket', label: 'paket' },
];

const VAT_OPTIONS = [
  { value: '22',  label: '22 %' },
  { value: '9.5', label: '9,5 %' },
  { value: '0',   label: '0 %' },
];

const PRESET_SERVICES: Record<string, ServiceRow[]> = {
  hisnik: [
    { name: 'Diagnostika in ogled', unit: 'kos', priceEur: 45, vatRate: 22 },
    { name: 'Popravilo vodovodne napeljave', unit: 'ura', priceEur: 55, vatRate: 22 },
    { name: 'Zamenjava pip / ventilov', unit: 'kos', priceEur: 80, vatRate: 22 },
    { name: 'Mala elektro dela', unit: 'ura', priceEur: 60, vatRate: 22 },
    { name: 'Slikarska dela', unit: 'm2', priceEur: 8, vatRate: 22 },
    { name: 'Pleskanje prostorov', unit: 'm2', priceEur: 12, vatRate: 22 },
  ],
  vodovod: [
    { name: 'Zamenjava umivalnika', unit: 'kos', priceEur: 120, vatRate: 22 },
    { name: 'Zamenjava WC školjke', unit: 'kos', priceEur: 150, vatRate: 22 },
    { name: 'Popravilo odtoka', unit: 'kos', priceEur: 80, vatRate: 22 },
    { name: 'Plinska napeljava (ura)', unit: 'ura', priceEur: 75, vatRate: 22 },
    { name: 'Inštalacija tuš kabine', unit: 'kos', priceEur: 250, vatRate: 22 },
  ],
  elektrika: [
    { name: 'Zamenjava vtičnice / stikala', unit: 'kos', priceEur: 35, vatRate: 22 },
    { name: 'Postavitev novega tokokroga', unit: 'ura', priceEur: 70, vatRate: 22 },
    { name: 'Zamenjava razdelilnika', unit: 'kos', priceEur: 400, vatRate: 22 },
    { name: 'Montaža razsvetljave', unit: 'kos', priceEur: 45, vatRate: 22 },
    { name: 'Pregled in meritve', unit: 'kos', priceEur: 90, vatRate: 22 },
  ],
  default: [
    { name: 'Storitev 1', unit: 'kos', priceEur: 0, vatRate: 22 },
    { name: 'Storitev 2', unit: 'ura', priceEur: 0, vatRate: 22 },
  ],
};

function getPreset(tradeType: string): ServiceRow[] {
  return PRESET_SERVICES[tradeType] ?? PRESET_SERVICES['default'];
}

function ServicesStep() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { services, setServices, setStep } = useOnboardingStore();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<ServicesValues>({
    resolver: zodResolver(servicesSchema),
    defaultValues: {
      tradeType: (services.tradeType as ServicesValues['tradeType']) ?? 'hisnik',
      services:  (services.services as ServiceRow[]) ?? getPreset('hisnik'),
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'services' });
  void watch('tradeType'); // trigger re-render ob spremembi tipa obrti

  function onTradeTypeChange(newType: string) {
    setValue('tradeType', newType as ServicesValues['tradeType'], { shouldValidate: true });
    setValue('services', getPreset(newType), { shouldValidate: true });
  }

  function onSubmit(data: ServicesValues) {
    setServices(data);
    setStep('voice');
    void navigate({ to: '/onboarding/voice' });
  }

  function goBack() {
    setStep('banking');
    void navigate({ to: '/onboarding/banking' });
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-6">
          <h2 className="font-display text-display-sm text-forge">{t('services.heading')}</h2>
          <p className="text-sm text-steel mt-1">{t('services.subheading')}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
          <Field id="tradeType" label={t('services.fields.trade_type')} required>
            <Controller
              name="tradeType"
              control={control}
              render={({ field }) => (
                <Select
                  id="tradeType"
                  options={TRADE_TYPE_OPTIONS}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    onTradeTypeChange(e.target.value);
                  }}
                />
              )}
            />
          </Field>

          {/* Service rows */}
          <div className="space-y-2">
            {/* Header */}
            <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-medium text-steel px-1">
              <span className="col-span-5">{t('services.fields.service_name')}</span>
              <span className="col-span-2">{t('services.fields.unit')}</span>
              <span className="col-span-2">{t('services.fields.price')}</span>
              <span className="col-span-2">{t('services.fields.vat_rate')}</span>
              <span className="col-span-1" />
            </div>

            {fields.map((field, idx) => (
              <div key={field.id} className="grid grid-cols-12 gap-2 items-start">
                <div className="col-span-12 sm:col-span-5">
                  <Controller
                    name={`services.${idx}.name`}
                    control={control}
                    render={({ field: f, fieldState }) => (
                      <Input
                        placeholder={t('services.fields.service_name')}
                        error={fieldState.error?.message}
                        {...f}
                      />
                    )}
                  />
                </div>

                <div className="col-span-4 sm:col-span-2">
                  <Controller
                    name={`services.${idx}.unit`}
                    control={control}
                    render={({ field: f }) => (
                      <Select options={UNIT_OPTIONS} value={f.value} onChange={f.onChange} />
                    )}
                  />
                </div>

                <div className="col-span-4 sm:col-span-2">
                  <Controller
                    name={`services.${idx}.priceEur`}
                    control={control}
                    render={({ field: f, fieldState }) => (
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        error={fieldState.error?.message}
                        className="font-mono"
                        {...f}
                        onChange={(e) => f.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                </div>

                <div className="col-span-3 sm:col-span-2">
                  <Controller
                    name={`services.${idx}.vatRate`}
                    control={control}
                    render={({ field: f }) => (
                      <Select
                        options={VAT_OPTIONS}
                        value={String(f.value)}
                        onChange={(e) => f.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                </div>

                <div className="col-span-1 flex items-center justify-center pt-1">
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="text-steel hover:text-rust transition-colors"
                    aria-label={t('services.remove_row')}
                    disabled={fields.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}

            {errors.services && typeof errors.services === 'object' && 'message' in errors.services && (
              <p className="text-xs text-rust">{String(errors.services.message)}</p>
            )}

            <button
              type="button"
              onClick={() => append({ name: '', unit: 'kos', priceEur: 0, vatRate: 22 })}
              className="flex items-center gap-1.5 text-sm text-copper hover:text-amber-700 transition-colors mt-2"
            >
              <PlusCircle className="h-4 w-4" />
              {t('services.add_row')}
            </button>
          </div>

          <div className="flex justify-between pt-2">
            <Button type="button" variant="ghost" onClick={goBack}>
              {t('actions.back', { ns: 'common' })}
            </Button>
            <Button type="submit" variant="primary" size="lg" disabled={!isValid}>
              {t('actions.next', { ns: 'common' })}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
